import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, dateTR, numberTR } from "../lib/format";
import { vehicleCost, vehicleProfit } from "../lib/constants";
import { CarFront } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

export default function SalesHistory() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/vehicles");
        setVehicles(res.data);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sold = useMemo(
    () =>
      vehicles
        .filter((v) => v.durum === "Satıldı")
        .sort((a, b) => (a.satisTarihi < b.satisTarihi ? 1 : -1)),
    [vehicles],
  );

  const totals = useMemo(() => {
    const ciro = sold.reduce((s, v) => s + (v.satisFiyati || 0), 0);
    const kar = sold.reduce((s, v) => s + vehicleProfit(v), 0);
    return { ciro, kar, adet: sold.length };
  }, [sold]);

  return (
    <div data-testid="sales-page">
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
          Satış Geçmişi
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
          Satılan Araçlar
        </h1>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Toplam Satış
          </div>
          <div className="font-display font-black text-2xl text-slate-900 num-tabular mt-1">
            {numberTR(totals.adet)}
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Toplam Ciro
          </div>
          <div className="font-display font-black text-2xl text-slate-900 num-tabular mt-1">
            {currencyTRY(totals.ciro)}
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Toplam Net Kâr
          </div>
          <div
            className={`font-display font-black text-2xl num-tabular mt-1 ${
              totals.kar >= 0 ? "text-emerald-700" : "text-rose-700"
            }`}
          >
            {currencyTRY(totals.kar)}
          </div>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-64 rounded-2xl" />
      ) : sold.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white/40 p-14 text-center">
          <CarFront className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <div className="font-display font-bold text-slate-900">
            Henüz satış yok
          </div>
          <div className="text-sm text-stone-500 mt-1">
            Bir aracı satışa çıkardığınızda burada listelenir.
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-stone-500 border-b border-stone-200 bg-stone-50/60">
            <div className="col-span-3">Araç</div>
            <div className="col-span-2">Alıcı</div>
            <div className="col-span-2">Satış Tarihi</div>
            <div className="col-span-2 text-right">Maliyet</div>
            <div className="col-span-1 text-right">Fiyat</div>
            <div className="col-span-2 text-right">Net Kâr</div>
          </div>
          <div className="divide-y divide-stone-100">
            {sold.map((v) => {
              const kar = vehicleProfit(v);
              return (
                <div
                  key={v.id}
                  data-testid={`sold-${v.plaka.replace(/\s/g, "")}`}
                  className="px-5 py-4 md:grid md:grid-cols-12 md:gap-3 md:items-center"
                >
                  <div className="md:col-span-3">
                    <div className="font-semibold text-slate-900">
                      {v.marka} {v.model}
                    </div>
                    <div className="text-xs text-stone-500">
                      {v.yil} · {v.plaka}
                    </div>
                  </div>
                  <div className="md:col-span-2 text-sm text-stone-700 mt-1 md:mt-0">
                    <div>{v.aliciAdi}</div>
                    <div className="text-xs text-stone-500">
                      {v.aliciTelefon}
                    </div>
                  </div>
                  <div className="md:col-span-2 text-sm text-stone-600 num-tabular mt-1 md:mt-0">
                    {dateTR(v.satisTarihi)}
                  </div>
                  <div className="md:col-span-2 md:text-right num-tabular text-sm text-stone-700 mt-1 md:mt-0">
                    {currencyTRY(vehicleCost(v))}
                  </div>
                  <div className="md:col-span-1 md:text-right num-tabular text-sm text-slate-900 font-semibold mt-1 md:mt-0">
                    {currencyTRY(v.satisFiyati)}
                  </div>
                  <div
                    className={`md:col-span-2 md:text-right font-display font-black num-tabular mt-1 md:mt-0 ${
                      kar >= 0 ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {currencyTRY(kar)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
